<template>
  <div v-if="show" class="app-modal" role="presentation">
    <div class="app-modal__backdrop" @click="emitClose"></div>

    <section
      class="app-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sale-create-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="sale-create-modal-title">{{ modalTitle }}</h3>
          <p>{{ modalSubtitle }}</p>
        </div>
        <button
          class="app-modal__close"
          type="button"
          :aria-label="closeAriaLabel"
          @click="emitClose"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__body">
        <aside class="app-modal__menu" :aria-label="sectionsAriaLabel">
          <button
            v-for="section in sections"
            :key="section.key"
            class="app-modal__menu-item"
            :class="{ active: activeSectionKey === section.key }"
            type="button"
            @click="activeSectionKey = section.key"
          >
            <span class="app-modal__menu-icon">
              <i class="fa" :class="section.icon" aria-hidden="true"></i>
            </span>
            <span class="app-modal__menu-copy">
              <strong>{{ section.title }}</strong>
              <small>{{ section.description }}</small>
            </span>
          </button>
        </aside>

        <div class="app-modal__content">
          <form class="app-modal__form" @submit.prevent="submitForm">
            <SaleModalSectionGeneral
              :active="activeSectionKey === 'general'"
              :form="form"
              :options="options"
              :is-edit-mode="isEditMode"
              :show-code-field="showCodeField"
              :is-advertising-enabled="isAdvertisingEnabled"
              :customer-mode="customerMode"
              :is-flat-rate-mode="isFlatRateMode"
              :first-installment-options="firstInstallmentOptions"
              :tax-preview="taxPreview"
              :v$="v$"
            />

            <SaleModalSectionConfiguration
              :active="activeSectionKey === 'configuration'"
              :form="form"
              :options="options"
              :tag-options="tagOptions"
              @add-tag="addTag"
            />

            <SaleModalSectionDetails
              :active="activeSectionKey === 'details'"
              :form="form"
            />

            <SaleModalSectionQuote
              :active="activeSectionKey === 'quote'"
              :form="form"
              :options="options"
              @change-quote-mode="setQuoteMode"
              @quote-file-change="onQuoteFileChange"
            />

            <footer class="app-modal__footer">
              <div class="app-modal__footer-left">
                <button
                  v-if="isEditMode"
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--delete sale-modal__delete-btn"
                  :title="deleteButtonLabel"
                  :aria-label="deleteButtonLabel"
                  @click="emitDelete"
                >
                  <i class="fa fa-trash" aria-hidden="true"></i>
                </button>
              </div>
              <div class="app-modal__footer-right">
                <button type="button" class="btn btn-light" @click="emitClose">{{ cancelButtonLabel }}</button>
                <button type="submit" class="btn btn-primary">
                  {{ submitting ? submittingLabel : submitButtonLabel }}
                </button>
              </div>
            </footer>
          </form>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import SaleModalSectionGeneral from '@/components/sales/modal/SaleModalSections/SaleModalSectionGeneral.vue';
import SaleModalSectionConfiguration from '@/components/sales/modal/SaleModalSections/SaleModalSectionConfiguration.vue';
import SaleModalSectionQuote from '@/components/sales/modal/SaleModalSections/SaleModalSectionQuote.vue';
import SaleModalSectionDetails from '@/components/sales/modal/SaleModalSections/SaleModalSectionDetails.vue';
import {
  createSaleModalState,
  saleModalComputed,
} from '@/composables/sales/modal/useSaleModalState';
import { saleModalValidations } from '@/composables/sales/modal/useSaleModalValidation';
import { saleModalWatch } from '@/composables/sales/modal/useSaleModalWatch';
import { saleModalMethods } from '@/composables/sales/modal/useSaleModalMethods';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
  name: 'SaleModal',
  components: {
    SaleModalSectionGeneral,
    SaleModalSectionConfiguration,
    SaleModalSectionQuote,
    SaleModalSectionDetails,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    initialData: {
      type: Object,
      default: null,
    },
    initialSectionKey: {
      type: String,
      default: 'general',
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    advertisingEnabled: {
      type: Boolean,
      default: true,
    },
    customerMode: {
      type: String,
      default: 'single',
    },
    saleKind: {
      type: String,
      default: 'business',
    },
    serviceFilterMode: {
      type: String,
      default: 'auto',
    },
  },
  emits: ['close', 'submit', 'delete'],
  data: createSaleModalState,
  computed: saleModalComputed,
  validations: saleModalValidations,
  setup() {
    const v$ = useVuelidate();

    return { v$ };
  },
  watch: saleModalWatch,
  beforeUnmount() {
    this.clearTaxPreviewDebounce();
  },
  methods: saleModalMethods,
};
</script>

<style scoped>
.app-modal__footer-left {
  margin-right: auto;
}

.sale-modal__delete-btn {
  min-height: 38px;
  height: 38px;
  width: 44px;
  min-width: 44px;
  padding: 0 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>

